import "flag-icons/css/flag-icons.min.css";
import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import ComingSoon from "@/pages/ComingSoon";
import { useLaunchMode } from "@/hooks/useLaunchMode";
import Landing from "@/pages/Landing";
import Legal from "@/pages/Legal";
import Install from "@/pages/Install";
import InstallNudge from "@/components/InstallNudge";
import { Toaster } from "sonner";

// Heavy app pages are code-split so unauthenticated visitors on the landing
// page never download the map/app bundle.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const PublicTrip = lazy(() => import("@/pages/PublicTrip"));
const Billing = lazy(() => import("@/pages/Billing"));
const PaymentStatus = lazy(() => import("@/pages/PaymentStatus"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const Settings = lazy(() => import("@/pages/Settings"));
const YearReview = lazy(() => import("@/pages/YearReview"));
const TripPrint = lazy(() => import("@/pages/TripPrint"));
const PublicPassport = lazy(() => import("@/pages/PublicPassport"));
const AdminMembers = lazy(() => import("@/pages/AdminMembers"));
const PublicPin = lazy(() => import("@/pages/PublicPin"));

function PageLoader() {
  return (
    <div className="min-h-screen bg-[#f7f5f0] flex items-center justify-center">
      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#5c5c5c]">Unfolding the map…</p>
    </div>
  );
}

/** Root: logged-in users get the app, visitors get the landing page. */
function HomeRoute() {
  const { user } = useAuth();
  const launchMode = useLaunchMode();
  if (user === null || (!user && launchMode === null)) return <PageLoader />; // auth / launch check in flight
  if (user) return <Dashboard />;
  return launchMode ? <ComingSoon /> : <Landing />;
}

/** While in coming-soon mode, registration is closed — send visitors to the splash page. */
function RegisterRoute() {
  const launchMode = useLaunchMode();
  if (launchMode === null) return <PageLoader />;
  return launchMode ? <Navigate to="/" replace /> : <Register />;
}

/** Google OAuth return lands on any route with #session_id=… — handle it before normal routing. */
function AppRoutes() {
  const location = useLocation(); // reactive hash (window.location.hash is not)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<RegisterRoute />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/terms" element={<Legal kind="terms" />} />
              <Route path="/privacy" element={<Legal kind="privacy" />} />
              <Route path="/install" element={<Install />} />
              <Route path="/t/:token" element={<PublicTrip />} />
              <Route path="/p/:token" element={<PublicPin />} />
              <Route path="/pp/:token" element={<PublicPassport />} />
              <Route
                path="/trips/:id/print"
                element={
                  <ProtectedRoute>
                    <TripPrint />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/year/:year"
                element={
                  <ProtectedRoute>
                    <YearReview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminMembers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing"
                element={
                  <ProtectedRoute>
                    <Billing />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment/success"
                element={
                  <ProtectedRoute>
                    <PaymentStatus mode="success" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment/cancel"
                element={
                  <ProtectedRoute>
                    <PaymentStatus mode="cancel" />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<HomeRoute />} />
              {/* Unknown URLs land back on home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <InstallNudge />
          <Suspense fallback={<PageLoader />}>
            <AppRoutes />
          </Suspense>
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: "#f7f5f0",
                color: "#1f1f1f",
                border: "1px solid #d5cfc1",
                borderRadius: 0,
                fontFamily: "IBM Plex Sans, sans-serif",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
